
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Edit, Trash2, GripVertical } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import FeaturedProductsGrid from "./FeaturedProductsGrid";
import ProductFormDialog from "./ProductFormDialog";
import IconPickerDialog from "./IconPickerDialog";

const productSchema = z.object({
  icon_name: z.string().min(1, "Icon is required"),
  name: z.string()
    .min(1, "Name is required")
    .max(30, "Product name must be 30 characters or less for optimal display"),
  category: z.string()
    .min(1, "Category is required")
    .max(20, "Category must be 20 characters or less for optimal display"),
  short_description: z.string()
    .max(100, "Description must be 100 characters or less")
    .optional(),
  is_active: z.boolean().default(true),
  sort_order: z.number().min(0).default(0),
  product_id: z.string().nullable().optional(),
});

type ProductFormData = z.infer<typeof productSchema>;

type ProductInsertData = {
  icon_name: string;
  name: string;
  category: string;
  short_description?: string;
  is_active: boolean;
  product_id?: string | null;
  sort_order: number;
};

const FeaturedProductsManager = () => {
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState("");
  const queryClient = useQueryClient();

  const { data: products, isLoading } = useQuery({
    queryKey: ['hero-featured-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hero_featured_products')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  const { data: allProducts } = useQuery({
    queryKey: ['all-products-for-admin-select'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name')
        .order('name', { ascending: true });
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      icon_name: "",
      name: "",
      category: "",
      short_description: "",
      is_active: true,
      sort_order: 0,
      product_id: null,
    }
  });

  const mutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      const payload = { 
        ...data, 
        product_id: data.product_id === 'none' ? null : data.product_id 
      };
      if (editingProduct) {
        const { error } = await supabase
          .from('hero_featured_products')
          .update(payload)
          .eq('id', editingProduct.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('hero_featured_products')
          .insert(payload as ProductInsertData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hero-featured-products'] });
      queryClient.invalidateQueries({ queryKey: ['heroData'] });
      toast.success(editingProduct ? 'Product updated' : 'Product added');
      handleCloseForm();
    },
    onError: (error) => {
      toast.error(editingProduct ? `Failed to update product: ${error.message}` : `Failed to add product: ${error.message}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('hero_featured_products')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hero-featured-products'] });
      toast.success('Product deleted');
    },
    onError: () => {
      toast.error('Failed to delete product');
    }
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: any[]) => {
      const updates = items.map((item, index) => ({
        id: item.id,
        sort_order: index
      }));
      
      for (const update of updates) {
        await supabase
          .from('hero_featured_products')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hero-featured-products'] });
      toast.success('Product order updated');
    }
  });

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    form.reset({
      icon_name: "",
      name: "",
      category: "",
      short_description: "",
      is_active: true,
      sort_order: 0,
      product_id: null,
    });
    setSelectedIcon("");
  };

  const handleEdit = (product: any) => {
    setEditingProduct(product);
    setSelectedIcon(product.icon_name);
    form.reset({
      icon_name: product.icon_name,
      name: product.name,
      category: product.category,
      short_description: product.short_description || "",
      is_active: product.is_active,
      sort_order: product.sort_order,
      product_id: product.product_id,
    });
    setShowForm(true);
  };

  const handleFormClose = () => {
    handleCloseForm();
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination || !products) return;

    const items = Array.from(products);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    reorderMutation.mutate(items);
  };

  const onSubmit = (data: ProductFormData) => {
    mutation.mutate(data);
  };

  // Get common Lucide icons for the picker
  const commonIcons = [
    'Diamond', 'Gem', 'Watch', 'Circle', 'Heart', 'Star', 'Crown', 'Award',
    'Gift', 'Package', 'ShoppingBag', 'Sparkles', 'Zap', 'Shield', 'Sun', 'Moon'
  ];

  const handleIconPickerSelect = (icon: string) => {
    setSelectedIcon(icon);
    form.setValue("icon_name", icon);
  };

  const IconComponent = selectedIcon ? (LucideIcons as any)[selectedIcon] : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Featured Products</CardTitle>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Product
        </Button>
      </CardHeader>
      <CardContent>
        {!products || products.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">No featured products found</p>
            <Button onClick={() => setShowForm(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Add First Product
            </Button>
          </div>
        ) : (
          <FeaturedProductsGrid
            products={products}
            onEdit={handleEdit}
            onDelete={(id) => deleteMutation.mutate(id)}
            onDragEnd={handleDragEnd}
          />
        )}

        {/* Product Form Dialog */}
        <ProductFormDialog
          open={showForm}
          isLoading={mutation.isPending}
          onClose={handleFormClose}
          onSubmit={form.handleSubmit(onSubmit)}
          onIconPickerOpen={() => setShowIconPicker(true)}
          selectedIcon={selectedIcon}
          setSelectedIcon={setSelectedIcon}
          form={form}
          products={allProducts || []}
        />

        {/* Icon Picker Dialog */}
        <IconPickerDialog
          open={showIconPicker}
          onOpenChange={setShowIconPicker}
          selectedIcon={selectedIcon}
          setSelectedIcon={setSelectedIcon}
          onPick={handleIconPickerSelect}
          commonIcons={commonIcons}
          setFormIconName={(icon) => form.setValue('icon_name', icon)}
        />
      </CardContent>
    </Card>
  );
};

export default FeaturedProductsManager;
